import React from 'react';
import Button from '../../../../components/form-control/Button';
import usePolicy from '../../../../hooks/policyHook';
import { Policy } from '../../../../models/policy/Policy';
import { FleetPolicy } from '../../../../models/policy/FleetPolicy';
import { isFleetPolicy, showToast } from '../../../../utils/miscFunctions';
import { approveFleetPolicy } from '../../../../api/requests';
import { useQueryClient } from '@tanstack/react-query';

type Props = {
  policy: Policy | FleetPolicy | undefined;
  onApproved: () => void;
  onCancel: () => void;
  // When true, route the approval through POST /admin/policies/{n}/self-approve
  // instead of the standard /approve endpoint. The self-approve endpoint
  // additionally gates on Intermediary.issuanceMode (MID_LIVE / INSURER_API
  // only); a 403 surfaces here as an error toast. Defaults to false so the
  // existing approve flow is unchanged.
  selfApprove?: boolean;
}

const PolicyApproval = ({ policy, onApproved, onCancel, selfApprove = false }: Props) => {
  const { approveSinglePolicy, selfApproveSinglePolicy } = usePolicy();
  const policyNumber = policy && "policyNumber" in policy ? policy?.policyNumber :  policy && "ref" in policy ? policy?.ref : '';
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      let response: any;
      if (isFleetPolicy(policy)) {
        // Fleet self-approval is a follow-up; for now fleet uses the
        // standard approve endpoint regardless of the selfApprove flag.
        response = await approveFleetPolicy(policy?.fleetNumber ?? '');
      } else if (selfApprove) {
        response = await selfApproveSinglePolicy(String(policyNumber));
      } else {
        response = await approveSinglePolicy(String(policyNumber));
      }

      showToast.success(response?.message || "Policy approved successfully");
      queryClient.invalidateQueries({ queryKey: ['fetchCustomer'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCustomerIndividualPolicies'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCustomerFleetPolicies'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCustomerNonMotorPolicies'] });
      
      
      onApproved();
    } catch (error: any) {
      showToast.error(error?.description);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl dark:bg-darkBg">
      <div className="p-4">
        <div className="py-3 dark:text-darkBg-mainText">
          <p>
            {selfApprove
              ? "Self Approval activates this policy immediately via your MID / API integration without going through the insurer's review queue. You confirm that all of the details on this policy are accurate."
              : "By approving this policy, you agree that all of the details provided by the policy's owner is accurate as of this day. Do you agree to approve this ?"}
          </p>
        </div>

        <div className="flex gap-4">
          <Button
            label="Cancel"
            onClick={onCancel}
            disabled={submitting}
            fullWidth
            bgColor='secondary'
          />

          <Button
            label={selfApprove ? "Self Approve" : "Approve"}
            fullWidth
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
            bgColor='primary'
          />
        </div>


        
      </div>
    </div>
  );
};

export default PolicyApproval;
